import { _lockUIandExecute } from '../../other/ui-lock.js';
import {
  activeV6FrameConfigVersion,
  getDealerMacroVersion,
  hasOptimizedBlobStorage,
  runBootLoaderEvents,
  setGlobalV6Version,
  v6ConfigEntryPoint,
  v6ConfigManager,
  v6FrameUserMode,
  V6OfflineCause,
  v6SupportsVersion,
  versionToInteger
} from './v6-config.js';
import { ApiCommunications } from '../webmodule-interop.js';
import { isEmptyOrSpace } from '../../other/ui/string-helper-functions.js';
import { litModalScreenFactoryImpl } from '../../other/ui/modal-factory-lit.js';
import { modalScreenFactoryImpl } from '../../other/ui/modal-factory.js';
import { sharedExecute } from '../../other/common/helpers/callbacks.js';
import { showError } from '../../other/ui/show-error.js';
import { userSecurity } from '../../other/api/user-security.js';

// eslint-disable-next-line no-var -- var declared as it is replaced by
declare var __localdebug__: string;
export const isLocalV6Debug = __localdebug__ === 'true';

let _stale = false;

export function v6bootloaderStale() {
  _stale = true;
}

/**
 * Reason codes for V6 script loader failures
 */
export enum V6LoaderFailureReason {
  /** No failure - load was successful */
  None = 'none',
  /** Supplier is offline or unreachable */
  SupplierOffline = 'supplier-offline',
  /** Version string is empty or invalid */
  InvalidVersion = 'invalid-version',
  /** Script failed to load from CDN */
  ScriptLoadFailed = 'script-load-failed',
  /** V6 config did not initialize correctly after load */
  ConfigInitFailed = 'config-init-failed',
  /** Loaded version does not support optimized blob storage */
  UnsupportedVersion = 'unsupported-version'
}

/**
 * Result of the V6 frame config script loader
 */
export interface V6LoaderResult {
  /** Whether the load was successful */
  success: boolean;
  /** The version that was loaded (or attempted) */
  version?: string;
  /** Reason for failure if success is false */
  failureReason?: V6LoaderFailureReason;
  /** Offline cause from the supplier (if applicable) */
  offlineCause?: V6OfflineCause;
  /** Message from the supplier or error details */
  message?: string;
  /** The underlying error if one occurred */
  error?: Error;
}

let scriptElement: HTMLScriptElement | null = null;
let _loadedVersion: string | undefined = undefined;

/**
 * Wrapped loader that prevents concurrent executions.
 * Use this as the public API.
 */
export const v6FrameConfigScriptLoader = sharedExecute(v6FrameConfigScriptLoaderInternal);

/**
 * Internal implementation of the V6 frame config script loader.
 * Loads the V6 config script dynamically, with version checking and cleanup.
 * Returns a compound result with success/failure and extended information.
 */
async function v6FrameConfigScriptLoaderInternal(comms: ApiCommunications): Promise<V6LoaderResult> {
  const isLocalV6 = isLocalV6Debug;

  // Already loaded and not stale - use v6ConfigManager for reliable check
  if (!_stale && _loadedVersion && v6ConfigManager()?.ver(_loadedVersion)) {
    return { success: true, version: _loadedVersion };
  }

  // Only fetch dealer version when we need it (stale check or first load)
  const dealerVersion = await getDealerMacroVersion(comms);
  const requiredVersion = isLocalV6 ? (activeV6FrameConfigVersion() ?? '') : (dealerVersion?.configVersion ?? '');

  // Check if supplier is offline or version is invalid
  if (isEmptyOrSpace(requiredVersion) && !isLocalV6) {
    return {
      success: false,
      failureReason:
        dealerVersion?.offlineCause !== undefined
          ? V6LoaderFailureReason.SupplierOffline
          : V6LoaderFailureReason.InvalidVersion,
      offlineCause: dealerVersion?.offlineCause ?? V6OfflineCause.Connection,
      message: dealerVersion?.offlineMessage
    };
  }

  // Already have this version loaded - just mark not stale and exit
  if (v6ConfigManager()?.ver(requiredVersion)) {
    _stale = false;
    _loadedVersion = requiredVersion;
    return { success: true, version: requiredVersion };
  }

  // Different version already loaded - cleanup and reload
  if (scriptElement && _loadedVersion && _loadedVersion !== requiredVersion) {
    scriptElement.remove();
    globalThis.v6ConfigManager?.dispose?.();
    globalThis.v6ConfigManager = undefined;
    scriptElement = null;
    _loadedVersion = undefined;
  }

  // Need to load the script
  const v6Script = document.createElement('script');
  scriptElement = v6Script;

  setGlobalV6Version(requiredVersion === '' ? 0 : versionToInteger(requiredVersion), requiredVersion);

  const v6Source = isLocalV6
    ? `./v6/v6config-entrypoint.js`
    : `https://webmodule-softtech.azureedge.net/v6config/${requiredVersion}/v6config-entrypoint.js`;

  try {
    await new Promise<void>((resolve, reject) => {
      v6Script.onload = () => {
        _stale = false;
        _loadedVersion = requiredVersion;
        const versionToUse = isLocalV6 ? (activeV6FrameConfigVersion() ?? 'v0.0.0') : requiredVersion;
        setGlobalV6Version(versionToInteger(versionToUse), versionToUse);
        const v6Events = v6ConfigEntryPoint(versionToUse)?.events;
        if (!v6Events) {
          reject(new Error(`V6 Config ${requiredVersion} did not load correctly`));
          return;
        }

        v6Events.setApiInjector(() => comms);
        v6Events.setErrorDialogEventHandler(showError);
        if (!v6SupportsVersion('v2.0.0')) {
          v6Events.setModalScreenFactory(modalScreenFactoryImpl, litModalScreenFactoryImpl);
          v6Events.setUILockAndExecuteEvent(_lockUIandExecute);
        }

        v6Events.setUserMode(userSecurity().isPowerUser() ? v6FrameUserMode.powerUser : v6FrameUserMode.standardUser);
        resolve();
      };

      v6Script.onerror = event => {
        console.error(`Failed to load V6 script from ${v6Source}:`, event);
        if (event instanceof ErrorEvent) {
          console.error('Error message:', event.message);
          console.error('Error filename:', event.filename);
          console.error('Error lineno:', event.lineno);
          console.error('Error colno:', event.colno);
          console.error('Error stack:', event.error?.stack);
        }

        fetch(v6Source)
          .then(response => {
            if (!response.ok) {
              console.error(`Network response was not ok: ${response.status} ${response.statusText}`);
            } else {
              console.log('Script can be fetched directly, but failed to load via script tag');
            }
          })
          .catch(err => {
            console.error('Fetch attempt also failed:', err);
          });

        reject(new Error(`Failed to load V6 script from ${v6Source}`));
      };

      v6Script.setAttribute('src', v6Source);
      document.body.appendChild(v6Script);
    });

    await runBootLoaderEvents();

    if (!hasOptimizedBlobStorage()) {
      return {
        success: false,
        version: requiredVersion,
        failureReason: V6LoaderFailureReason.UnsupportedVersion,
        message: 'V6 version does not support optimized blob storage'
      };
    }

    return { success: true, version: requiredVersion };
  } catch (error) {
    console.error('V6 script load failed:', error);
    // Clean up on failure
    scriptElement?.remove();
    scriptElement = null;
    _loadedVersion = undefined;

    const err = error instanceof Error ? error : new Error(String(error));
    const isConfigInit = err.message.includes('did not load correctly');

    return {
      success: false,
      version: requiredVersion,
      failureReason: isConfigInit ? V6LoaderFailureReason.ConfigInitFailed : V6LoaderFailureReason.ScriptLoadFailed,
      message: err.message,
      error: err
    };
  }
}
